// Moteur tarifaire unique — utilisé pour la création/modification d'un RDV,
// la finalisation et l'estimation publique. Une seule logique partout.

export const PRICING_TYPES = {
  fixe: "Prix fixe",
  a_partir_de: "À partir de",
  sur_devis: "Sur devis",
};

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const eur = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

// Montant d'une ligne de prestation (quantité × (tarif + supplément gabarit)).
// Retourne null si « sur devis » non chiffré (jamais transformé en 0 €).
export function lineAmount(li) {
  if (li.unit_price === null || li.unit_price === undefined || li.unit_price === "") return null;
  const qty = Number(li.quantity) || 1;
  const supp = li.non_vehicle ? 0 : Number(li.vehicle_supplement) || 0;
  return round2(qty * ((Number(li.unit_price) || 0) + supp));
}

// Total complet du RDV. Formule :
// prestations + suppléments véhicule (inclus dans les lignes) + suppléments manuels
// + déplacement − réductions = TOTAL.
export function computePricing({ line_items = [], supplements = [], discounts = [], travel = null, deposit = 0 } = {}) {
  let servicesTotal = 0;
  let hasPending = false;
  for (const li of line_items) {
    const amt = lineAmount(li);
    if (amt === null) { hasPending = true; continue; }
    servicesTotal += amt;
  }
  const supplementsTotal = (supplements || []).reduce(
    (s, x) => s + (Number(x.quantity) || 1) * (Number(x.unit_price) || 0), 0);
  const travelAmount = travel ? Number(travel.amount) || 0 : 0;
  const discountTotal = (discounts || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const total = round2(servicesTotal + supplementsTotal + travelAmount - discountTotal);
  const dep = Number(deposit) || 0;
  return {
    servicesTotal: round2(servicesTotal),
    supplementsTotal: round2(supplementsTotal),
    travelAmount: round2(travelAmount),
    discountTotal: round2(discountTotal),
    total,
    deposit: round2(dep),
    remaining: round2(total - dep),
    hasPending,
  };
}

// Suggestion de frais de déplacement d'après la config entreprise et le lieu.
// Chez le client = 1 aller-retour ; Récupération + livraison = 2 aller-retours.
export function suggestTravel({ mode, locationType, distanceKm, flat, perKm, roundTrip = true, freeKm = 0, minAmount = 0, tiers = [], recupMultiplier = 2 }) {
  if (!locationType || locationType === "mon_local" || !mode || mode === "aucun" || mode === "manuel") return 0;
  const visits = locationType === "recuperation_livraison" ? (Number(recupMultiplier) || 2) : 1;
  const tripMul = roundTrip ? 2 : 1;
  let amount = 0;
  if (mode === "forfait") amount = (Number(flat) || 0) * visits;
  else if (mode === "km") {
    const billable = Math.max(0, (Number(distanceKm) || 0) - (Number(freeKm) || 0));
    amount = billable * tripMul * (Number(perKm) || 0) * visits;
  } else if (mode === "paliers") {
    const km = Number(distanceKm) || 0;
    const sorted = [...(tiers || [])].filter((t) => t && t.max_km !== "" && t.max_km != null).sort((a, b) => Number(a.max_km) - Number(b.max_km));
    let tierAmt = 0;
    for (const t of sorted) { if (km <= Number(t.max_km)) { tierAmt = Number(t.amount) || 0; break; } }
    if (!tierAmt && sorted.length) tierAmt = Number(sorted[sorted.length - 1].amount) || 0;
    amount = tierAmt * visits;
  }
  if (minAmount && (mode === "km" || mode === "paliers" || mode === "forfait") && amount < Number(minAmount)) amount = Number(minAmount);
  return round2(amount);
}

// Prix affiché publiquement selon le type de tarification.
export function publicPriceLabel(service) {
  if (service.public_price_visible === false) return null;
  const t = service.pricing_type || (service.price == null ? "sur_devis" : "fixe");
  if (t === "sur_devis") return "Sur devis";
  if (service.price == null) return "Sur devis";
  if (t === "a_partir_de") return `À partir de ${eur(service.price)}`;
  return eur(service.price);
}

// Construit une ligne de RDV à partir d'une prestation du catalogue.
export function lineFromService(service, categoryId) {
  const type = service.pricing_type || (service.price == null ? "sur_devis" : "fixe");
  const base = service.price != null ? Number(service.price) : null;
  const unit = type === "sur_devis" ? (base != null ? base : null) : (base != null ? base : null);
  const supp = !service.non_vehicle && categoryId
    ? Number((service.vehicle_supplements || {})[categoryId] || 0) : 0;
  return {
    service_id: service.id,
    name: service.name,
    pricing_type: type,
    non_vehicle: !!service.non_vehicle,
    quantity: 1,
    unit_price: unit,
    vehicle_supplement: supp,
    sur_devis_pending: type === "sur_devis" && unit == null,
    description: service.description || null,
  };
}
